// Admin Broadcast - Send notifications to users (FRESH BUILD)
import React, { useState, useEffect } from 'react';
import { Send, Users, AlertTriangle, MessageSquare, Lightbulb, Megaphone, ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Database } from '@/database/Database';
import { NotificationData } from '@/entities/Notification';
import { useAuth } from '@/contexts/AuthContext';

interface User {
  id: string;
  email: string;
  name: string;
}

export function BroadcastNotificationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [notificationType, setNotificationType] = useState<NotificationData['type']>('system');
  const [priority, setPriority] = useState<NotificationData['priority']>('normal');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [recentBroadcasts, setRecentBroadcasts] = useState<any[]>([]);
  const [dismissMode, setDismissMode] = useState<'manual' | 'auto'>('manual'); // manual = until dismissed, auto = timed
  const [autoDismissSeconds, setAutoDismissSeconds] = useState<number>(10);

  useEffect(() => {
    console.log('🎯 NUCLEAR OPTION - BroadcastNotificationPage mounted - COMPLETELY NEW FILE');
    console.log('👤 Current user:', user);
    if (!user) {
      console.log('⚠️ No user found in auth context - this might be why page is blank!');
    }
    checkAdminAccess();
  }, [user]);

  const checkAdminAccess = async () => {
    if (!user) {
      console.log('❌ No user logged in, redirecting...');
      navigate('/login');
      return;
    }

    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      const isAuthorized = profile?.role === 'admin' || profile?.role === 'super_admin';
      
      if (!isAuthorized) {
        console.log('❌ User is not admin/super_admin, redirecting...');
        alert('⛔ Access Denied: Admin privileges required');
        navigate('/');
        return;
      }

      console.log(`✅ Access confirmed - Role: ${profile?.role}`);
      setIsAdmin(true);
      loadUsers();
      loadRecentBroadcasts();
    } catch (error) {
      console.error('❌ Failed to check admin access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    console.log('🚀 loadUsers() starting...');
    try {
      const db = new Database();
      let allUsers: User[] = [];
      const userIds = new Set<string>(); // Track unique user IDs

      // Method 1: Load from Supabase profiles
      try {
        console.log('📊 Method 1: Loading from Supabase profiles...');
        const { supabase } = await import('@/lib/supabase');
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('user_id, email, name')
          .order('created_at', { ascending: false });

        if (!error && profiles && profiles.length > 0) {
          profiles.forEach(p => {
            if (p.user_id && !userIds.has(p.user_id)) {
              userIds.add(p.user_id);
              allUsers.push({
                id: p.user_id,
                email: p.email || 'no-email',
                name: p.name || p.email?.split('@')[0] || 'Unknown',
              });
            }
          });
          console.log(`✅ Loaded ${profiles.length} users from Supabase profiles`);
        } else if (error) {
          console.warn('⚠️ Supabase profiles error:', error.message);
        }
      } catch (supabaseError) {
        console.warn('⚠️ Supabase profiles not accessible');
      }

      // Method 2: Load from entities table (User entity type) - ADD TO EXISTING
      console.log('📊 Method 2: Loading from entities table...');
      try {
        const result = await db.query(
          `SELECT id, data FROM entities WHERE entity_type = 'User' ORDER BY created_at DESC`
        );

        result.rows.forEach((row: any) => {
          try {
            const data = JSON.parse(row.data);
            const userId = data.userId || data.user_id || data.id || row.id;
            if (data.email && !userIds.has(userId)) {
              userIds.add(userId);
              allUsers.push({
                id: userId,
                email: data.email,
                name: data.name || data.displayName || data.email?.split('@')[0] || 'Unknown',
              });
            }
          } catch (e) {
            // Skip malformed rows
          }
        });
        console.log(`✅ Loaded ${result.rows.length} rows from entities table (${allUsers.length - userIds.size} new users added)`);
      } catch (entityError) {
        console.warn('⚠️ Entities table query failed:', entityError);
      }

      // Method 3: Try Supabase Auth admin API - ADD TO EXISTING
      console.log('📊 Method 3: Trying Supabase Auth admin API...');
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { users: authUsers }, error } = await supabase.auth.admin.listUsers();
        
        if (!error && authUsers && authUsers.length > 0) {
          authUsers.forEach(u => {
            if (!userIds.has(u.id)) {
              userIds.add(u.id);
              allUsers.push({
                id: u.id,
                email: u.email || 'no-email',
                name: u.user_metadata?.name || u.email?.split('@')[0] || 'Unknown',
              });
            }
          });
          console.log(`✅ Loaded ${authUsers.length} users from Supabase Auth (${allUsers.length - userIds.size} new users added)`);
        }
      } catch (authError) {
        console.warn('⚠️ Supabase Auth admin API not accessible (this is normal)');
      }

      if (allUsers.length === 0) {
        console.error('❌ No users found in any source!');
        alert('⚠️ No users found. Users must sign up first.');
      } else {
        console.log(`✅ TOTAL: ${allUsers.length} unique users loaded from ${userIds.size} sources`);
        console.log('Users:', allUsers);
      }
      
      setUsers(allUsers);
    } catch (error) {
      console.error('❌ Failed to load users:', error);
      alert('Failed to load users. Check console for details.');
    }
  };

  const loadRecentBroadcasts = async () => {
    try {
      const db = new Database();
      const result = await db.query(
        `SELECT id, data, created_at FROM entities 
         WHERE entity_type = 'Notification' 
         ORDER BY created_at DESC LIMIT 10`
      );

      if (result && result.rows && result.rows.length > 0) {
        setRecentBroadcasts(result.rows.map((row: any) => ({
          ...JSON.parse(row.data),
          id: row.id,
          createdAt: row.created_at,
        })));
      } else {
        setRecentBroadcasts([]);
      }
    } catch (error) {
      console.error('Failed to load broadcasts:', error);
      setRecentBroadcasts([]);
    }
  };

  const handleSend = async () => {
    if (!title || !message) {
      alert('Please fill in title and message');
      return;
    }

    if (!selectedUser) {
      alert('Please select a recipient');
      return;
    }

    setIsSending(true);
    try {
      const db = new Database();
      const targetUsers = selectedUser === 'all' 
        ? users.map(u => u.id) 
        : [selectedUser];

      console.log(`📢 Sending to ${targetUsers.length} user(s)...`);

      const timestamp = new Date().toISOString();
      
      for (const userId of targetUsers) {
        const notification = {
          userId: String(userId),
          type: notificationType,
          title,
          message,
          priority,
          read: false,
          acknowledged: false,
          dismissMode,
          autoDismissSeconds: dismissMode === 'auto' ? autoDismissSeconds : null,
          createdAt: timestamp,
        };

        await db.query(
          `INSERT INTO entities (entity_type, data, created_at, updated_at)
           VALUES (?, ?, ?, ?)`,
          [
            'Notification',
            JSON.stringify(notification),
            timestamp,
            timestamp,
          ]
        );
      }
      
      console.log(`✅ Sent notification to ${targetUsers.length} user(s)`);
      alert(`✅ Notification sent to ${targetUsers.length} user(s)!`);
      
      // Reset form
      setTitle('');
      setMessage('');
      setSelectedUser('');
      loadRecentBroadcasts();
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert('❌ Failed to send notification');
    } finally {
      setIsSending(false);
    }
  };

  const typeIcons = {
    message: MessageSquare,
    warning: AlertTriangle,
    suggestion: Lightbulb,
    due_date: AlertTriangle,
    system: Megaphone,
  };

  const TypeIcon = typeIcons[notificationType];

  // Show loading state while checking admin access
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 animate-pulse" />
          <p>Verifying admin access...</p>
          <p className="text-sm text-slate-400 mt-2">User: {user?.email || 'Not logged in'}</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not admin (will redirect)
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p>Access Denied</p>
          <p className="text-sm text-slate-400 mt-2">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Megaphone className="w-8 h-8 text-purple-400" />
              Send Notification
            </h1>
            <p className="text-slate-400 mt-1">
              Broadcast messages to users ({users.length} total)
            </p>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700 p-6 mb-6">
          {/* Recipient Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Users className="w-4 h-4 inline mr-2" />
              Recipient
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
            >
              <option value="">Select a user...</option>
              <option value="all">📢 All Users ({users.length})</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Type Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Notification Type
              </label>
              <select
                value={notificationType}
                onChange={(e) => setNotificationType(e.target.value as NotificationData['type'])}
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              >
                <option value="message">💬 Message</option>
                <option value="warning">⚠️ Warning</option>
                <option value="suggestion">💡 Suggestion Response</option>
                <option value="due_date">📅 Due Date Alert</option>
                <option value="system">📢 System Announcement</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as NotificationData['priority'])}
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              >
                <option value="low">Low (auto-dismiss)</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent (requires action)</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title..."
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Notification message..."
              rows={4}
              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Dismiss Behavior */}
          <div className="mb-6 space-y-4">
            <label className="block text-sm font-medium text-slate-300">
              Dismiss Behavior
            </label>
            
            <div className="space-y-3">
              {/* Manual Dismiss */}
              <label className="flex items-center gap-3 p-3 bg-slate-900/30 rounded-lg border border-slate-700 cursor-pointer hover:border-purple-500/50 transition-colors">
                <input
                  type="radio"
                  name="dismissMode"
                  value="manual"
                  checked={dismissMode === 'manual'}
                  onChange={(e) => setDismissMode(e.target.value as 'manual' | 'auto')}
                  className="w-4 h-4 text-purple-500"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Stay Until Dismissed</div>
                  <div className="text-slate-400 text-sm">User must manually dismiss the notification</div>
                </div>
              </label>

              {/* Auto Dismiss */}
              <label className="flex items-center gap-3 p-3 bg-slate-900/30 rounded-lg border border-slate-700 cursor-pointer hover:border-purple-500/50 transition-colors">
                <input
                  type="radio"
                  name="dismissMode"
                  value="auto"
                  checked={dismissMode === 'auto'}
                  onChange={(e) => setDismissMode(e.target.value as 'manual' | 'auto')}
                  className="w-4 h-4 text-purple-500"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Auto-Dismiss After Time</div>
                  <div className="text-slate-400 text-sm">Notification disappears automatically</div>
                </div>
              </label>

              {/* Auto-dismiss time selector */}
              {dismissMode === 'auto' && (
                <div className="ml-7 pl-3 border-l-2 border-purple-500/30">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Auto-dismiss after:
                  </label>
                  <select
                    value={autoDismissSeconds}
                    onChange={(e) => setAutoDismissSeconds(Number(e.target.value))}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          {title && message && (
            <div className="mb-6 p-4 bg-slate-900/50 rounded-lg border border-slate-600">
              <p className="text-xs text-slate-400 mb-2">Preview:</p>
              <div className="flex items-start gap-3">
                <TypeIcon className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold text-sm">{title}</h3>
                  <p className="text-slate-300 text-sm mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={isSending || !selectedUser || !title || !message}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-slate-600 disabled:to-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
            {isSending ? 'Sending...' : 'Send Notification'}
          </button>
        </div>

        {/* Recent Broadcasts */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Recent Broadcasts</h2>
          <div className="space-y-3">
            {recentBroadcasts.length === 0 ? (
              <p className="text-slate-400 text-sm">No broadcasts yet</p>
            ) : (
              recentBroadcasts.map((broadcast) => (
                <div
                  key={broadcast.id}
                  className="p-3 bg-slate-900/30 rounded-lg border border-slate-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-white font-medium text-sm">{broadcast.title}</h3>
                      <p className="text-slate-400 text-xs mt-1 line-clamp-1">{broadcast.message}</p>
                    </div>
                    <span className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(broadcast.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
